import { FivetranServiceEnum, stripeSchema } from "@repo/fivetran/types";
import { Icon } from "@repo/ui/components/icon";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/popover";
import { Button } from "@repo/ui/ui/button";
import { Calendar } from "@repo/ui/ui/calendar";
import { Field, FieldLabel } from "@repo/ui/ui/field";
import { format } from "date-fns";

import {
  DestinationField,
  DestinationSetupField,
  SchemaField,
} from "./connection-fields";
import { ConnectionFormShell } from "./connection-form-shell";
import {
  type ConnectionFormProps,
  useConnectionForm,
} from "./use-connection-form";

/**
 * Human-readable labels for Stripe's `historical_sync_time_frame` config
 * values, keyed by the raw Fivetran value so the trigger renders the label
 * (e.g. "All time") instead of the raw enum ("ALL_TIME").
 */
const SYNC_TIME_FRAME_LABELS: Record<string, string> = {
  ALL_TIME: "All time",
  DATE_LIMIT: "Start date",
};

const SYNC_TIME_FRAME_OPTIONS = Object.entries(SYNC_TIME_FRAME_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export const StripeFormPage = ({
  onConnected,
  onCancel,
}: ConnectionFormProps = {}) => {
  const { form, embedded, formId } = useConnectionForm({
    service: FivetranServiceEnum.enum.stripe,
    schema: stripeSchema,
    defaultConfig: {
      schema: FivetranServiceEnum.enum.stripe,
      api_key: "",
      support_connected_accounts_sync: false,
      historical_sync_time_frame: "ALL_TIME",
      backward_sync_limit: "",
    },
    onConnected,
  });

  return (
    <ConnectionFormShell
      form={form}
      formId={formId}
      icon="stripe"
      submitLabel="Create"
      embedded={embedded}
      onCancel={onCancel}
    >
      <DestinationField form={form} />
      <DestinationSetupField form={form} />
      <SchemaField form={form} />

      <form.AppField name="config.api_key">
        {(field) => (
          <field.PasswordField
            label="API Key"
            placeholder="pk_live_XXXXXXXXXXXXXXXXXXXXXXXX"
          />
        )}
      </form.AppField>

      <form.AppField name="config.support_connected_accounts_sync">
        {(field) => (
          <field.SwitchField
            label="Connected accounts"
            description={
              <>
                Enable support for{" "}
                <a
                  href="https://stripe.com/docs/api/connected_accounts"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  connected accounts
                </a>{" "}
                sync.
              </>
            }
          />
        )}
      </form.AppField>

      <div className="grid grid-cols-2 gap-4">
        <form.AppField name="config.historical_sync_time_frame">
          {(field) => (
            <field.SelectField
              label="Sync time frame"
              placeholder="Select time frame"
              options={SYNC_TIME_FRAME_OPTIONS}
              formatValue={(value) => SYNC_TIME_FRAME_LABELS[value] ?? value}
            />
          )}
        </form.AppField>
        <form.Subscribe
          selector={(state) => state.values.config.historical_sync_time_frame}
        >
          {(historicalSyncTimeFrame) => (
            <form.AppField name="config.backward_sync_limit">
              {(field) => {
                const value = field.state.value as string;
                const isDisabled = historicalSyncTimeFrame !== "DATE_LIMIT";
                return (
                  <Field className="flex-1">
                    <FieldLabel htmlFor={field.name}>
                      Sync start date
                    </FieldLabel>
                    <Popover>
                      <PopoverTrigger
                        render={
                          <Button
                            variant="outline"
                            className="w-full justify-between font-normal"
                            disabled={isDisabled}
                          />
                        }
                      >
                        {value
                          ? format(new Date(value), "MM/dd/yyyy")
                          : "Select date"}
                        <Icon name="chevron-down" className="size-4" />
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto overflow-hidden p-0"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          captionLayout="dropdown"
                          selected={value ? new Date(value) : undefined}
                          onSelect={(date) =>
                            field.handleChange(
                              format(date ?? new Date(), "yyyy/MM/dd"),
                            )
                          }
                          disabled={isDisabled}
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>
                );
              }}
            </form.AppField>
          )}
        </form.Subscribe>
      </div>
    </ConnectionFormShell>
  );
};
